import type {
  Page,
  PageQuery,
  BackofficeMeResponseDto,
  PlatformDashboardDto,
  VisitIntentReportDto,
  MerchantInterestRowDto,
  MerchantInterestSummaryDto,
  MerchantInterestQuery,
  MerchantInterestSort,
  MerchantOpportunityRowDto,
  MerchantOpportunityDetailDto,
  OpportunitySummaryDto,
  OpportunityFunnelDto,
  OpportunityQuery,
  MerchantOpportunityOutcome,
  InterestScoreSettingDto,
  InterestScoreSettingRequestDto,
  MetricsRange,
  GeocodingBackfillReport,
  MerchantDto,
  MerchantRequestDto,
  MerchantStatus,
  MerchantPartnershipStatus,
  PartnershipDto,
  PartnershipEventDto,
  PartnershipTransitionRequestDto,
  MerchantFeatureDto,
  MerchantFeatureRequest,
  BackofficeFeatureRowDto,
  FeatureOverviewQuery,
  FeatureOverviewSummaryDto,
  FeatureDensityDto,
  MerchantFeatureScope,
  QuickMerchantRequestDto,
  MerchantDuplicateDto,
  MerchantMergeRequestDto,
  MerchantMergeResultDto,
  MerchantImportRequestDto,
  MerchantImportPreviewDto,
  MerchantImportBatchDto,
  MerchantImportReportDto,
  MerchantImageUploadRequestDto,
  MerchantImageUploadResponseDto,
  BackofficeMerchantEmployeeDto,
  BackofficeMerchantEmployeeRequestDto,
  BackofficeMerchantEmployeeStatusRequestDto,
  BackofficeMerchantAccessProfileOptionDto,
  ExternalLinkPlatformOptionDto,
  CustomerDto,
  CustomerPaymentDto,
  CustomerStatus,
  AmenityDto,
  AmenityCreateRequestDto,
  AmenityUpdateRequestDto,
  PaymentMethodDto,
  PaymentMethodCreateRequestDto,
  PaymentMethodUpdateRequestDto,
  SearchTermReportDto,
  CategoryDto,
  CategoryRequestDto,
  LocalityDto,
  CollectionDto,
  CollectionRequestDto,
  CollectionStatus,
  FeedDto,
  FeedRequestDto,
  FeedStatus,
  BackofficeTrailDto,
  TrailRequestDto,
  TrailPreviewRequestDto,
  TrailPreviewDto,
  MerchantDiscoveryOptionsDto,
  LocalityRequestDto,
  PromotionDto,
  PromotionModerationDto,
  PromotionRuleTemplateDto,
  PromotionRuleTemplateRequestDto,
  PlanDto,
  PlanRequestDto,
  PassPlanDto,
  PassPlanRequestDto,
  InfluencerDto,
  InfluencerOverviewDto,
  InfluencerRateDto,
  InfluencerRateRequestDto,
  CreateInfluencerRequestDto,
  UpdateInfluencerRequestDto,
  CommissionDto,
  CommissionStatus,
  PayoutDto,
  PayoutStatus,
  SubscriptionDto,
  InvoiceDto,
  AuditLogDto,
  MerchantBillingAnalysisDto,
  MerchantAvailabilityAnalysisDto,
  BackofficeEmployeeDto,
  BackofficeEmployeeRequestDto,
  BackofficePermissionCode,
  BackofficeAccessProfileDto,
  BackofficeAccessProfileRequestDto,
  AccessProfilePermissionsUpdateDto,
  BackofficeEmployeeAccessDetailDto,
  EmailChannelStatusDto,
  LeadDto,
  LeadBoardDto,
  LeadStatusUpdateDto,
  MessagingChannelStatusDto,
  MessagingInstanceDto,
  MessagingInstanceCreateRequestDto,
  MessagingInstanceCreatedDto,
  MessagingInstanceUpdateRequestDto,
  MessagingQrCodeDto,
  MessagingConnectionDto,
  MessagingTestRequestDto,
  MessageTemplateDto,
  MessageTemplateRequestDto,
  MessageTagDto,
  TemplatePreviewRequestDto,
  TemplatePreviewDto,
  ContactListDto,
  ContactListRequestDto,
  ContactListMemberDto,
  ContactImportRequestDto,
  ContactImportReportDto,
  ContactListMergeRequestDto,
  ContactListMergeReportDto,
  MessagingContactDto,
  ContactBlockRequestDto,
  WhatsappStatus,
  WhatsappCheckRequestDto,
  WhatsappCheckReportDto,
  MessagingOptOutDto,
  MessagingOptOutRequestDto,
  MessageCampaignDto,
  MessageCampaignRequestDto,
  MessageCampaignStatus,
  MessageDispatchDto,
  MessageDispatchStatus,
  CampaignCountersDto,
  BackofficeMerchantClaimDto,
  MerchantClaimDecisionRequestDto,
  MerchantClaimRevokeRequestDto,
  MerchantClaimStatus,
  BackofficeProfileEditDto,
  ProfileEditDecisionRequestDto,
  MerchantProfileEditStatus,
  BackofficeClaimEvidenceDto,
  ClaimEvidenceAccessDto,
  ClaimEvidenceContent,
} from '@kardume/types';
import api from './http';

/**
 * Os filtros da visão interna de interesse (card 258).
 *
 * Campo ausente é "não filtrar", e por isso cada um é omitido em vez de ir como
 * `undefined`/vazio: `partner=` na query string chega ao core como string vazia
 * e não como ausência, e o recorte viria errado sem ninguém ver.
 */
const merchantInterestParams = (query: MerchantInterestQuery = {}) => ({
  ...(query.from ? { from: query.from } : {}),
  ...(query.to ? { to: query.to } : {}),
  ...(query.city ? { city: query.city } : {}),
  ...(query.categoryId ? { categoryId: query.categoryId } : {}),
  ...(query.partner === undefined ? {} : { partner: query.partner }),
  ...(query.claimed === undefined ? {} : { claimed: query.claimed }),
  ...(query.platform ? { platform: query.platform } : {}),
});

/**
 * O recorte da tela Destaques.
 *
 * Cada campo só entra quando tem valor: mandar `localityId=` vazio faria o
 * backend filtrar por "cidade nenhuma" e a tela mostraria zero destaques com
 * cara de "não há destaque em lugar nenhum".
 */
const featureParams = (query: FeatureOverviewQuery = {}) => ({
  ...(query.scope ? { scope: query.scope } : {}),
  ...(query.localityId ? { localityId: query.localityId } : {}),
  ...(query.categoryId ? { categoryId: query.categoryId } : {}),
  ...(query.status ? { status: query.status } : {}),
  ...(query.from ? { from: query.from } : {}),
  ...(query.to ? { to: query.to } : {}),
});

/**
 * Os filtros do ranking de oportunidades (card 238).
 *
 * `minScore` só vai quando é maior que zero: zero já é "sem filtro", e mandá-lo
 * na query string só polui a URL que o operador copia para pedir ajuda.
 */
const opportunityParams = (query: OpportunityQuery = {}) => ({
  ...(query.city ? { city: query.city } : {}),
  ...(query.categoryId ? { categoryId: query.categoryId } : {}),
  ...(query.minScore ? { minScore: query.minScore } : {}),
  ...(query.windowDays ? { windowDays: query.windowDays } : {}),
});

const toParams = (query: PageQuery = {}) => ({
  page: query.page ?? 0,
  size: query.size ?? 20,
  ...(query.sort ? { sort: query.sort } : {}),
  ...(query.query ? { query: query.query } : {}),
});

/**
 * The file name the server sent in `Content-Disposition`.
 *
 * The core writes `attachment; filename*=UTF-8''<percent-encoded>` so that
 * accents survive the header, which means the value has to be decoded before
 * it reaches a download link - otherwise "contrato-social-jos%C3%A9.pdf" is
 * what lands on the analyst's disk.
 */
const fileNameFromDisposition = (disposition: unknown): string => {
  const header = typeof disposition === 'string' ? disposition : '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.trim());
    } catch {
      return encoded.trim();
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header)?.[1];
  return plain?.trim() || 'documento';
};

/**
 * Backoffice app API (Kardume internal team).
 *
 * Base: kardume_urbano_core. See docs/endpoints-core.md.
 */
export const backofficeApi = {
  getMe(): Promise<BackofficeMeResponseDto> {
    return api.get<BackofficeMeResponseDto>('/v1/backoffice/auth/me').then((r) => r.data);
  },

  /* Dashboard */
  getDashboard(range: MetricsRange = '30d'): Promise<PlatformDashboardDto> {
    return api.get<PlatformDashboardDto>('/v1/backoffice/dashboard', { params: { range } }).then((r) => r.data);
  },

  /**
   * Intenção de visita agregada da plataforma (card 214): totais, as lojas mais
   * desejadas, as mais visitadas e o interesse por categoria.
   *
   * Só agrega - não existe, aqui nem no core, um caminho que devolva quem
   * salvou. Exige `ROLE_BACKOFFICE_REPORT_READ`, então a tela carrega isto
   * separado do painel: sem a permissão, este bloco falha sozinho em vez de
   * derrubar a página inteira.
   */
  getVisitIntentReport(): Promise<VisitIntentReportDto> {
    return api.get<VisitIntentReportDto>('/v1/backoffice/reports/visit-intent').then((r) => r.data);
  },

  /**
   * A visão interna do interesse por empresa (card 258, KU-05d).
   *
   * Cidade, categoria e o estado da relação comercial são os do **momento do
   * evento**, não os de hoje: é o que permite perguntar quanto interesse uma
   * empresa já tinha antes de virar parceira. Lê o agregado do core, então
   * continua respondendo por períodos que o evento cru (retenção de 30 dias)
   * já não cobre.
   *
   * `orderBy` e não `sort`: `sort` é o parâmetro de ordenação do Spring Data e
   * significa outra coisa no mesmo endpoint.
   */
  listMerchantInterest(
    query?: PageQuery & MerchantInterestQuery & { orderBy?: MerchantInterestSort },
  ): Promise<Page<MerchantInterestRowDto>> {
    return api
      .get<Page<MerchantInterestRowDto>>('/v1/backoffice/reports/merchant-interest', {
        params: { ...toParams(query), ...merchantInterestParams(query), ...(query?.orderBy ? { orderBy: query.orderBy } : {}) },
      })
      .then((r) => r.data);
  },

  /**
   * Totais, cidades e categorias do mesmo recorte.
   *
   * Chamada separada do ranking de propósito: trocar de cidade no gráfico não
   * precisa recarregar a lista paginada, e o bloco de cima continua na tela
   * enquanto a de baixo pagina.
   */
  getMerchantInterestSummary(query?: MerchantInterestQuery): Promise<MerchantInterestSummaryDto> {
    return api
      .get<MerchantInterestSummaryDto>('/v1/backoffice/reports/merchant-interest/summary', {
        params: merchantInterestParams(query),
      })
      .then((r) => r.data);
  },

  /**
   * O ranking de oportunidades comerciais (card 238, KU-29).
   *
   * Empresas NÃO parceiras ordenadas pelo score de interesse da última janela
   * calculada pelo job. Parceira ativa, empresa fora do ar no guia e quem já
   * foi marcada como "virou parceira" ou "não abordar" já vêm de fora - o
   * filtro é no banco. Filtrar aqui faria a paginação prometer 20 itens e
   * mostrar 14.
   *
   * Lista vazia enquanto o job não tiver rodado: é o estado normal do primeiro
   * dia, e não um erro.
   */
  listOpportunities(query?: PageQuery & OpportunityQuery): Promise<Page<MerchantOpportunityRowDto>> {
    return api
      .get<Page<MerchantOpportunityRowDto>>('/v1/backoffice/opportunities', {
        params: { ...toParams(query), ...opportunityParams(query) },
      })
      .then((r) => r.data);
  },

  getOpportunitySummary(query?: OpportunityQuery): Promise<OpportunitySummaryDto> {
    return api
      .get<OpportunitySummaryDto>('/v1/backoffice/opportunities/summary', { params: opportunityParams(query) })
      .then((r) => r.data);
  },

  getOpportunityFunnel(): Promise<OpportunityFunnelDto> {
    return api.get<OpportunityFunnelDto>('/v1/backoffice/opportunities/funnel').then((r) => r.data);
  },

  /**
   * O detalhe explicável: a decomposição do cálculo, a coorte e a evolução.
   *
   * 404 quando a empresa nunca teve score calculado - "existe mas não tem
   * número" não é uma tela.
   */
  getOpportunity(merchantId: string, windowDays?: number): Promise<MerchantOpportunityDetailDto> {
    return api
      .get<MerchantOpportunityDetailDto>(`/v1/backoffice/opportunities/${merchantId}`, {
        params: windowDays ? { windowDays } : {},
      })
      .then((r) => r.data);
  },

  /** Cria ou atualiza o lead desta empresa - um lead por empresa, nunca duplicado. */
  generateOpportunityLead(merchantId: string, windowDays?: number): Promise<MerchantOpportunityDetailDto> {
    return api
      .post<MerchantOpportunityDetailDto>(`/v1/backoffice/opportunities/${merchantId}/lead`, null, {
        params: windowDays ? { windowDays } : {},
      })
      .then((r) => r.data);
  },

  markOpportunityOutcome(
    merchantId: string,
    outcome: MerchantOpportunityOutcome,
    notes?: string,
  ): Promise<MerchantOpportunityDetailDto> {
    return api
      .patch<MerchantOpportunityDetailDto>(`/v1/backoffice/opportunities/${merchantId}/outcome`, { outcome, notes })
      .then((r) => r.data);
  },

  getInterestScoreSettings(): Promise<InterestScoreSettingDto> {
    return api.get<InterestScoreSettingDto>('/v1/backoffice/opportunities/settings').then((r) => r.data);
  },

  getInterestScoreSettingsHistory(): Promise<InterestScoreSettingDto[]> {
    return api
      .get<InterestScoreSettingDto[]>('/v1/backoffice/opportunities/settings/history')
      .then((r) => r.data);
  },

  /** Grava uma VERSÃO nova dos pesos. O efeito aparece na próxima passada do job. */
  updateInterestScoreSettings(request: InterestScoreSettingRequestDto): Promise<InterestScoreSettingDto> {
    return api
      .put<InterestScoreSettingDto>('/v1/backoffice/opportunities/settings', request)
      .then((r) => r.data);
  },

  /* Merchants */
  listMerchants(
    query?: PageQuery & { status?: MerchantStatus; partnershipStatus?: MerchantPartnershipStatus },
  ): Promise<Page<MerchantDto>> {
    return api
      .get<Page<MerchantDto>>('/v1/backoffice/merchants', {
        params: {
          ...toParams(query),
          ...(query?.status ? { status: query.status } : {}),
          // Card 226 - eixo separado de `status` de propósito: "aparece no
          // catálogo?" e "existe parceria?" são perguntas diferentes, e juntá-las
          // impediria a que o comercial de fato faz (quais parceiras estão fora
          // do ar).
          ...(query?.partnershipStatus ? { partnershipStatus: query.partnershipStatus } : {}),
        },
      })
      .then((r) => r.data);
  },

  /* --- Reivindicação de perfil (cards 234 / 252) --- */

  /**
   * A fila, do mais antigo para o mais novo.
   *
   * A ordenação é do servidor e não é opção de tela: a fila existe para que
   * ninguém espere demais, e o topo dela tem que ser sempre quem mais esperou.
   */
  listMerchantClaims(query?: PageQuery & { status?: MerchantClaimStatus }): Promise<Page<BackofficeMerchantClaimDto>> {
    return api
      .get<Page<BackofficeMerchantClaimDto>>('/v1/backoffice/merchant-claims', {
        params: {
          ...toParams(query),
          ...(query?.status ? { status: query.status } : {}),
        },
      })
      .then((r) => r.data);
  },

  getMerchantClaim(claimId: string): Promise<BackofficeMerchantClaimDto> {
    return api.get<BackofficeMerchantClaimDto>(`/v1/backoffice/merchant-claims/${claimId}`).then((r) => r.data);
  },

  /** Aprovar, recusar ou pedir informação. O servidor recusa sem motivo. */
  decideMerchantClaim(
    claimId: string,
    payload: MerchantClaimDecisionRequestDto,
  ): Promise<BackofficeMerchantClaimDto> {
    return api
      .post<BackofficeMerchantClaimDto>(`/v1/backoffice/merchant-claims/${claimId}/decision`, payload)
      .then((r) => r.data);
  },

  /** Tira o acesso de um responsável já aprovado. A empresa fica sem dono no mesmo ato. */
  revokeMerchantClaim(
    claimId: string,
    payload: MerchantClaimRevokeRequestDto,
  ): Promise<BackofficeMerchantClaimDto> {
    return api
      .post<BackofficeMerchantClaimDto>(`/v1/backoffice/merchant-claims/${claimId}/revoke`, payload)
      .then((r) => r.data);
  },

  /* --- Moderação de edições e evidências (cards 253 / 277, §37.11-12) --- */

  /**
   * A fila de alterações sensíveis, mais antiga primeiro.
   *
   * A ordenação é do servidor e não é opção de tela, pelo mesmo motivo da fila
   * de reivindicações: o topo tem que ser sempre quem mais esperou.
   */
  listProfileEdits(
    query?: PageQuery & { status?: MerchantProfileEditStatus },
  ): Promise<Page<BackofficeProfileEditDto>> {
    return api
      .get<Page<BackofficeProfileEditDto>>('/v1/backoffice/merchant-profile-edits', {
        params: {
          ...toParams(query),
          ...(query?.status ? { status: query.status } : {}),
        },
      })
      .then((r) => r.data);
  },

  getProfileEdit(editId: string): Promise<BackofficeProfileEditDto> {
    return api
      .get<BackofficeProfileEditDto>(`/v1/backoffice/merchant-profile-edits/${editId}`)
      .then((r) => r.data);
  },

  /**
   * Aprovar aplica o valor na página no instante da decisão. O motivo é
   * obrigatório nas duas - o servidor recusa sem ele.
   */
  decideProfileEdit(
    editId: string,
    payload: ProfileEditDecisionRequestDto,
  ): Promise<BackofficeProfileEditDto> {
    return api
      .post<BackofficeProfileEditDto>(
        `/v1/backoffice/merchant-profile-edits/${editId}/decision`,
        payload,
      )
      .then((r) => r.data);
  },

  /**
   * Os anexos de um pedido - **metadado apenas**.
   *
   * Nome, tipo, tamanho, prazo de guarda e quantas vezes cada um já foi aberto.
   * Listar não é abrir e não registra acesso; por isso esta chamada pode
   * acontecer ao abrir o detalhe, e `openClaimEvidence` não pode.
   */
  listClaimEvidences(claimId: string): Promise<BackofficeClaimEvidenceDto[]> {
    return api
      .get<BackofficeClaimEvidenceDto[]>(`/v1/backoffice/merchant-claims/${claimId}/evidences`)
      .then((r) => r.data);
  },

  /**
   * Abre o documento - e **registra a abertura**, no servidor, no ato.
   *
   * Só deve ser chamada por ação explícita de quem opera a tela. Pré-carregar
   * para "agilizar" registraria um acesso que ninguém pediu, com o nome de
   * quem só abriu a lista. Documento já expurgado responde 404.
   */
  openClaimEvidence(evidenceId: string): Promise<ClaimEvidenceContent> {
    return api
      .get<Blob>(`/v1/backoffice/merchant-claim-evidences/${evidenceId}/content`, {
        responseType: 'blob',
      })
      .then((r) => ({
        blob: r.data,
        fileName: fileNameFromDisposition(r.headers?.['content-disposition']),
      }));
  },

  /** A trilha de quem abriu o documento. Vive além do arquivo: sobrevive ao expurgo. */
  listClaimEvidenceAccesses(evidenceId: string): Promise<ClaimEvidenceAccessDto[]> {
    return api
      .get<ClaimEvidenceAccessDto[]>(`/v1/backoffice/merchant-claim-evidences/${evidenceId}/access`)
      .then((r) => r.data);
  },

  // --- Guia da cidade (card 231, regras §38) ---

  /** Cadastro rápido: ACTIVE sem parceria, com origem obrigatória. */
  quickCreateMerchant(payload: QuickMerchantRequestDto): Promise<MerchantDto> {
    return api.post<MerchantDto>('/v1/backoffice/merchants/guide', payload).then((r) => r.data);
  },
  /** A checagem de duplicidade que a tela faz antes de salvar. Avisa, não bloqueia. */
  findMerchantDuplicates(name: string, city?: string): Promise<MerchantDuplicateDto[]> {
    return api
      .get<MerchantDuplicateDto[]>('/v1/backoffice/merchants/guide/duplicates', {
        params: { name, ...(city ? { city } : {}) },
      })
      .then((r) => r.data);
  },
  /** Funde a duplicata nesta empresa, preservando favoritos e avaliações. */
  mergeMerchant(
    merchantId: string,
    payload: MerchantMergeRequestDto,
  ): Promise<MerchantMergeResultDto> {
    return api
      .post<MerchantMergeResultDto>(`/v1/backoffice/merchants/guide/${merchantId}/merge`, payload)
      .then((r) => r.data);
  },
  /** Pré-visualiza a importação. Nada é persistido. */
  previewMerchantImport(payload: MerchantImportRequestDto): Promise<MerchantImportPreviewDto> {
    return api
      .post<MerchantImportPreviewDto>('/v1/backoffice/merchants/guide/imports/preview', payload)
      .then((r) => r.data);
  },
  /** Registra o lote. O processamento é assíncrono. */
  registerMerchantImport(payload: MerchantImportRequestDto): Promise<MerchantImportBatchDto> {
    return api
      .post<MerchantImportBatchDto>('/v1/backoffice/merchants/guide/imports', payload)
      .then((r) => r.data);
  },
  listMerchantImports(query?: PageQuery): Promise<Page<MerchantImportBatchDto>> {
    return api
      .get<Page<MerchantImportBatchDto>>('/v1/backoffice/merchants/guide/imports', {
        params: toParams(query),
      })
      .then((r) => r.data);
  },
  getMerchantImportReport(batchId: string): Promise<MerchantImportReportDto> {
    return api
      .get<MerchantImportReportDto>(`/v1/backoffice/merchants/guide/imports/${batchId}`)
      .then((r) => r.data);
  },
  /** Antecipa o processamento em vez de esperar o próximo ciclo. */
  processMerchantImport(batchId: string): Promise<MerchantImportBatchDto> {
    return api
      .post<MerchantImportBatchDto>(
        `/v1/backoffice/merchants/guide/imports/${batchId}/process`,
        {},
      )
      .then((r) => r.data);
  },

  // --- Parceria e destaque (card 226, regras §37) ---

  /** Estado atual da parceria, com as transições possíveis a partir dele. */
  getPartnership(merchantId: string): Promise<PartnershipDto> {
    return api
      .get<PartnershipDto>(`/v1/backoffice/merchants/${merchantId}/partnership`)
      .then((r) => r.data);
  },
  /** Move a parceria. O motivo é obrigatório e vira histórico. */
  changePartnership(
    merchantId: string,
    payload: PartnershipTransitionRequestDto,
  ): Promise<PartnershipDto> {
    return api
      .post<PartnershipDto>(`/v1/backoffice/merchants/${merchantId}/partnership`, payload)
      .then((r) => r.data);
  },
  /** A timeline, do mais recente para o mais antigo. */
  listPartnershipHistory(merchantId: string): Promise<PartnershipEventDto[]> {
    return api
      .get<PartnershipEventDto[]>(`/v1/backoffice/merchants/${merchantId}/partnership/history`)
      .then((r) => r.data);
  },
  /** Destaques vigentes da empresa. */
  listMerchantFeatures(merchantId: string): Promise<MerchantFeatureDto[]> {
    return api
      .get<MerchantFeatureDto[]>(`/v1/backoffice/merchants/${merchantId}/partnership/features`)
      .then((r) => r.data);
  },
  /**
   * Cria um destaque.
   *
   * Recusado pelo core quando a empresa não tem parceria ativa - destaque é o
   * nível de cima da parceria, e destacar quem não é parceiro venderia
   * exposição a quem não tem relação nenhuma com a plataforma.
   */
  createMerchantFeature(merchantId: string, payload: MerchantFeatureRequest): Promise<MerchantFeatureDto> {
    return api
      .post<MerchantFeatureDto>(
        `/v1/backoffice/merchants/${merchantId}/partnership/features`,
        payload,
      )
      .then((r) => r.data);
  },
  /** Encerra um destaque agora. */
  endMerchantFeature(merchantId: string, featureId: string): Promise<MerchantFeatureDto> {
    return api
      .post<MerchantFeatureDto>(
        `/v1/backoffice/merchants/${merchantId}/partnership/features/${featureId}/end`,
        {},
      )
      .then((r) => r.data);
  },

  // --- Visão de destaques que atravessa as empresas (card 262) ---

  /**
   * O que está em destaque, onde e até quando.
   *
   * Paginada e filtrável por cidade, categoria, escopo, período e vigência. É a
   * pergunta inversa à do drawer, e a única que não dá para responder abrindo
   * empresa por empresa.
   */
  listFeatures(query?: PageQuery & FeatureOverviewQuery): Promise<Page<BackofficeFeatureRowDto>> {
    return api
      .get<Page<BackofficeFeatureRowDto>>('/v1/backoffice/features', {
        params: { ...toParams(query), ...featureParams(query) },
      })
      .then((r) => r.data);
  },

  /**
   * Vigentes, agendados e encerrados do mesmo recorte.
   *
   * Chamada separada da lista de propósito: paginar não recarrega os cartões, e
   * o `status` do filtro não entra aqui - um contador de agendados que zerasse
   * ao filtrar "vigentes" viraria eco do filtro.
   */
  getFeatureSummary(query?: Omit<FeatureOverviewQuery, 'status'>): Promise<FeatureOverviewSummaryDto> {
    return api
      .get<FeatureOverviewSummaryDto>('/v1/backoffice/features/summary', {
        params: featureParams(query),
      })
      .then((r) => r.data);
  },

  /**
   * Quantos destaques já disputam a vitrine em que o operador vai colocar mais
   * um.
   *
   * Conta os do contexto **e** os de plataforma inteira, que aparecem em toda
   * vitrine. Alimenta um aviso, nunca um bloqueio.
   */
  getFeatureDensity(
    scope: MerchantFeatureScope,
    context?: { localityId?: string | null; categoryId?: string | null },
  ): Promise<FeatureDensityDto> {
    return api
      .get<FeatureDensityDto>('/v1/backoffice/features/density', {
        params: {
          scope,
          ...(context?.localityId ? { localityId: context.localityId } : {}),
          ...(context?.categoryId ? { categoryId: context.categoryId } : {}),
        },
      })
      .then((r) => r.data);
  },
  // --- Geolocalização (card 150) ---

  /**
   * Quantas empresas ativas ainda estão sem coordenada.
   *
   * É o número que diz se a vitrine "mais próximas" é confiável naquela cidade:
   * com metade da base sem ponto, ela ordena metade do catálogo e empurra o
   * resto para o fim.
   */
  countMerchantsWithoutCoordinates(): Promise<number> {
    return api
      .get<{ pending: number }>('/v1/backoffice/merchants/geocoding/pending')
      .then((r) => r.data.pending);
  },
  /**
   * Resolve latitude/longitude a partir do CEP cadastrado.
   *
   * Devolve `false` quando o CEP não tem coordenada conhecida - caso comum
   * fora das capitais, e não erro: a tela avisa e a loja segue sem ponto.
   */
  geocodeMerchant(merchantId: string, overwrite = true): Promise<boolean> {
    return api
      .post<{ resolved: boolean }>(
        `/v1/backoffice/merchants/${merchantId}/geocode`,
        {},
        { params: { overwrite } },
      )
      .then((r) => r.data.resolved);
  },
  /** Geocodifica em lote quem está sem coordenada e devolve o relatório. */
  backfillGeocoding(limit?: number): Promise<GeocodingBackfillReport> {
    return api
      .post<GeocodingBackfillReport>(
        '/v1/backoffice/merchants/geocoding/backfill',
        {},
        { params: limit ? { limit } : undefined },
      )
      .then((r) => r.data);
  },
  getMerchant(id: string): Promise<MerchantDto> {
    return api.get<MerchantDto>(`/v1/backoffice/merchants/${id}`).then((r) => r.data);
  },
  /** Catalog of external sales channels - the only source of the platform list. */
  listExternalLinkPlatforms(): Promise<ExternalLinkPlatformOptionDto[]> {
    return api
      .get<ExternalLinkPlatformOptionDto[]>('/v1/backoffice/merchants/external-link-platforms')
      .then((r) => r.data);
  },
  createMerchant(payload: MerchantRequestDto): Promise<MerchantDto> {
    return api.post<MerchantDto>('/v1/backoffice/merchants', payload).then((r) => r.data);
  },
  updateMerchant(id: string, payload: MerchantRequestDto): Promise<MerchantDto> {
    return api.put<MerchantDto>(`/v1/backoffice/merchants/${id}`, payload).then((r) => r.data);
  },
  /**
   * Envia logomarca, capa ou imagem da galeria do lojista e devolve a URL
   * pública - que ainda precisa ser gravada no cadastro via `updateMerchant`.
   *
   * Espelha `adminApi.uploadProfileImage`, mas escopado por lojista: o operador
   * do backoffice age sobre a loja de outra pessoa, então o destino não pode
   * sair do token como sai no painel do lojista.
   */
  uploadMerchantImage(
    id: string,
    payload: MerchantImageUploadRequestDto,
  ): Promise<MerchantImageUploadResponseDto> {
    return api
      .post<MerchantImageUploadResponseDto>(`/v1/backoffice/merchants/${id}/images`, payload)
      .then((r) => r.data);
  },
  /**
   * Exclusão lógica do lojista. Junto vão a assinatura (cancelada também no
   * provedor de pagamento), os anúncios - senão a loja continua na vitrine do
   * site e do app - e o acesso da equipe ao painel. Cupons, cashback e faturas
   * emitidas são preservados.
   */
  deleteMerchant(id: string): Promise<void> {
    return api.delete(`/v1/backoffice/merchants/${id}`).then(() => undefined);
  },
  updateMerchantStatus(id: string, status: MerchantStatus): Promise<MerchantDto> {
    return api.patch<MerchantDto>(`/v1/backoffice/merchants/${id}/status`, { status }).then((r) => r.data);
  },
  approveMerchant(id: string): Promise<MerchantDto> {
    return api
      .post<MerchantDto>(`/v1/backoffice/merchants/${id}/onboarding/approve`, {})
      .then((r) => r.data);
  },
  rejectMerchant(id: string, reason: string): Promise<MerchantDto> {
    return api
      .post<MerchantDto>(`/v1/backoffice/merchants/${id}/onboarding/reject`, { reason })
      .then((r) => r.data);
  },
  getMerchantBillingAnalysis(id: string): Promise<MerchantBillingAnalysisDto> {
    return api
      .get<MerchantBillingAnalysisDto>(`/v1/backoffice/merchants/${id}/billing-analysis`)
      .then((r) => r.data);
  },
  getMerchantAvailabilityAnalysis(id: string): Promise<MerchantAvailabilityAnalysisDto> {
    return api
      .get<MerchantAvailabilityAnalysisDto>(`/v1/backoffice/merchants/${id}/availability-analysis`)
      .then((r) => r.data);
  },

  /* Equipe do lojista.
     Existe para a operação montar a equipe junto com o cadastro da loja, em vez
     de depender do lojista entrar no painel dele - que é o passo que trava o
     começo do uso. A senha temporária é gerada no servidor e vai por e-mail;
     nenhuma destas respostas a devolve. */
  listMerchantEmployees(merchantId: string): Promise<BackofficeMerchantEmployeeDto[]> {
    return api
      .get<BackofficeMerchantEmployeeDto[]>(`/v1/backoffice/merchants/${merchantId}/employees`)
      .then((r) => r.data);
  },
  /**
   * Perfis de acesso da loja, para escolher o do funcionário.
   *
   * Vem vazio com frequência: perfil é criado pelo lojista, no painel dele, e
   * loja recém-cadastrada não tem nenhum. Sem perfil, só dá para cadastrar
   * OWNER - a tela precisa dizer isso antes de o operador tentar.
   */
  listMerchantAccessProfiles(merchantId: string): Promise<BackofficeMerchantAccessProfileOptionDto[]> {
    return api
      .get<BackofficeMerchantAccessProfileOptionDto[]>(
        `/v1/backoffice/merchants/${merchantId}/employees/access-profiles`
      )
      .then((r) => r.data);
  },
  createMerchantEmployee(
    merchantId: string,
    payload: BackofficeMerchantEmployeeRequestDto
  ): Promise<BackofficeMerchantEmployeeDto> {
    return api
      .post<BackofficeMerchantEmployeeDto>(`/v1/backoffice/merchants/${merchantId}/employees`, payload)
      .then((r) => r.data);
  },
  updateMerchantEmployee(
    merchantId: string,
    employeeId: string,
    payload: BackofficeMerchantEmployeeRequestDto
  ): Promise<BackofficeMerchantEmployeeDto> {
    return api
      .put<BackofficeMerchantEmployeeDto>(
        `/v1/backoffice/merchants/${merchantId}/employees/${employeeId}`,
        payload
      )
      .then((r) => r.data);
  },
  /**
   * Gera credenciais temporárias novas e as envia por e-mail ao funcionário.
   *
   * `POST` e não `PUT` porque não é idempotente: cada chamada produz uma senha
   * DIFERENTE e invalida a anterior, além de derrubar as sessões abertas. Dois
   * cliques deixam válido só o segundo e-mail - por isso a tela confirma antes.
   *
   * A senha não volta na resposta; o retorno é o funcionário, para atualizar a
   * linha da tabela.
   */
  resetMerchantEmployeeCredentials(
    merchantId: string,
    employeeId: string
  ): Promise<BackofficeMerchantEmployeeDto> {
    return api
      .post<BackofficeMerchantEmployeeDto>(
        `/v1/backoffice/merchants/${merchantId}/employees/${employeeId}/reset-credentials`
      )
      .then((r) => r.data);
  },
  /** Ativar/bloquear sem passar pelo formulário inteiro (o PUT exige o corpo todo). */
  setMerchantEmployeeStatus(
    merchantId: string,
    employeeId: string,
    payload: BackofficeMerchantEmployeeStatusRequestDto
  ): Promise<BackofficeMerchantEmployeeDto> {
    return api
      .patch<BackofficeMerchantEmployeeDto>(
        `/v1/backoffice/merchants/${merchantId}/employees/${employeeId}/status`,
        payload
      )
      .then((r) => r.data);
  },

  /* Customers */
  listCustomers(query?: PageQuery & { status?: CustomerStatus }): Promise<Page<CustomerDto>> {
    return api
      .get<Page<CustomerDto>>('/v1/backoffice/customers', {
        params: { ...toParams(query), ...(query?.status ? { status: query.status } : {}) },
      })
      .then((r) => r.data);
  },
  getCustomer(id: string): Promise<CustomerDto> {
    return api.get<CustomerDto>(`/v1/backoffice/customers/${id}`).then((r) => r.data);
  },
  updateCustomerStatus(id: string, status: CustomerStatus): Promise<CustomerDto> {
    return api.patch<CustomerDto>(`/v1/backoffice/customers/${id}/status`, { status }).then((r) => r.data);
  },
  updateCustomerPlan(id: string, planId: string): Promise<CustomerDto> {
    return api.patch<CustomerDto>(`/v1/backoffice/customers/${id}/plan`, { planId }).then((r) => r.data);
  },
  /**
   * Exclusão lógica do cliente. O histórico de compras, cupons e cashback fica -
   * é dele que vive a contabilidade do lojista - mas a assinatura ativa é
   * encerrada para não continuar cobrando.
   */
  deleteCustomer(id: string): Promise<void> {
    return api.delete(`/v1/backoffice/customers/${id}`).then(() => undefined);
  },
  /** Histórico de compras do consumidor (cobranças do Kardume Pass), mais recentes primeiro. */
  listCustomerPayments(id: string, query?: PageQuery): Promise<Page<CustomerPaymentDto>> {
    return api
      .get<Page<CustomerPaymentDto>>(`/v1/backoffice/customers/${id}/payments`, { params: toParams(query) })
      .then((r) => r.data);
  },

  /* Categories */
  /* Promotion rule catalogue */
  listPromotionRuleTemplates(): Promise<PromotionRuleTemplateDto[]> {
    return api
      .get<PromotionRuleTemplateDto[]>('/v1/backoffice/promotion-rule-templates')
      .then((r) => r.data);
  },
  createPromotionRuleTemplate(payload: PromotionRuleTemplateRequestDto): Promise<PromotionRuleTemplateDto> {
    return api
      .post<PromotionRuleTemplateDto>('/v1/backoffice/promotion-rule-templates', payload)
      .then((r) => r.data);
  },
  updatePromotionRuleTemplate(
    id: string,
    payload: PromotionRuleTemplateRequestDto
  ): Promise<PromotionRuleTemplateDto> {
    return api
      .put<PromotionRuleTemplateDto>(`/v1/backoffice/promotion-rule-templates/${id}`, payload)
      .then((r) => r.data);
  },
  /**
   * Liga/desliga uma cláusula.
   *
   * O `DELETE` do core também só desativa; usamos o PATCH nos dois sentidos
   * para que a tela tenha uma operação só e o botão de reativar não precise
   * de um verbo diferente do de desativar.
   */
  setPromotionRuleTemplateStatus(id: string, active: boolean): Promise<PromotionRuleTemplateDto> {
    return api
      .patch<PromotionRuleTemplateDto>(`/v1/backoffice/promotion-rule-templates/${id}/status`, { active })
      .then((r) => r.data);
  },

  listCategories(query?: PageQuery): Promise<Page<CategoryDto>> {
    return api.get<Page<CategoryDto>>('/v1/backoffice/categories', { params: toParams(query) }).then((r) => r.data);
  },
  createCategory(payload: CategoryRequestDto): Promise<CategoryDto> {
    return api.post<CategoryDto>('/v1/backoffice/categories', payload).then((r) => r.data);
  },
  updateCategory(id: string, payload: CategoryRequestDto): Promise<CategoryDto> {
    return api.put<CategoryDto>(`/v1/backoffice/categories/${id}`, payload).then((r) => r.data);
  },
  deleteCategory(id: string): Promise<void> {
    return api.delete(`/v1/backoffice/categories/${id}`).then(() => undefined);
  },
  reorderCategories(orderedIds: string[]): Promise<void> {
    return api.put('/v1/backoffice/categories/reorder', { orderedIds }).then(() => undefined);
  },

  /* Localities (cities where Kardume operates) */
  listLocalities(query?: PageQuery & { active?: boolean }): Promise<Page<LocalityDto>> {
    return api
      .get<Page<LocalityDto>>('/v1/backoffice/localities', {
        params: { ...toParams(query), ...(query?.active !== undefined ? { active: query.active } : {}) },
      })
      .then((r) => r.data);
  },
  createLocality(payload: LocalityRequestDto): Promise<LocalityDto> {
    return api.post<LocalityDto>('/v1/backoffice/localities', payload).then((r) => r.data);
  },
  updateLocality(id: string, payload: LocalityRequestDto): Promise<LocalityDto> {
    return api.put<LocalityDto>(`/v1/backoffice/localities/${id}`, payload).then((r) => r.data);
  },
  deleteLocality(id: string): Promise<void> {
    return api.delete(`/v1/backoffice/localities/${id}`).then(() => undefined);
  },

  /* Amenities (catálogo de comodidades das lojas - card 305) */
  listAmenities(query?: PageQuery & { active?: boolean }): Promise<Page<AmenityDto>> {
    return api
      .get<Page<AmenityDto>>('/v1/backoffice/amenities', {
        params: { ...toParams(query), ...(query?.active !== undefined ? { active: query.active } : {}) },
      })
      .then((r) => r.data);
  },
  createAmenity(payload: AmenityCreateRequestDto): Promise<AmenityDto> {
    return api.post<AmenityDto>('/v1/backoffice/amenities', payload).then((r) => r.data);
  },
  updateAmenity(id: string, payload: AmenityUpdateRequestDto): Promise<AmenityDto> {
    return api.put<AmenityDto>(`/v1/backoffice/amenities/${id}`, payload).then((r) => r.data);
  },
  deleteAmenity(id: string): Promise<void> {
    return api.delete(`/v1/backoffice/amenities/${id}`).then(() => undefined);
  },

  /* Payment methods (catálogo de formas de pagamento das lojas - card 311) */
  listPaymentMethods(query?: PageQuery & { active?: boolean }): Promise<Page<PaymentMethodDto>> {
    return api
      .get<Page<PaymentMethodDto>>('/v1/backoffice/payment-methods', {
        params: { ...toParams(query), ...(query?.active !== undefined ? { active: query.active } : {}) },
      })
      .then((r) => r.data);
  },
  createPaymentMethod(payload: PaymentMethodCreateRequestDto): Promise<PaymentMethodDto> {
    return api.post<PaymentMethodDto>('/v1/backoffice/payment-methods', payload).then((r) => r.data);
  },
  updatePaymentMethod(id: string, payload: PaymentMethodUpdateRequestDto): Promise<PaymentMethodDto> {
    return api.put<PaymentMethodDto>(`/v1/backoffice/payment-methods/${id}`, payload).then((r) => r.data);
  },
  deletePaymentMethod(id: string): Promise<void> {
    return api.delete(`/v1/backoffice/payment-methods/${id}`).then(() => undefined);
  },

  /* Termos buscados na vitrine (card 346) */
  getSearchTerms(query?: {
    page?: number;
    size?: number;
    from?: string;
    to?: string;
    localityId?: string;
    onlyZeroResults?: boolean;
  }): Promise<SearchTermReportDto> {
    return api
      .get<SearchTermReportDto>('/v1/backoffice/search-terms', {
        params: {
          ...(query?.page !== undefined ? { page: query.page } : {}),
          ...(query?.size !== undefined ? { size: query.size } : {}),
          ...(query?.from ? { from: query.from } : {}),
          ...(query?.to ? { to: query.to } : {}),
          ...(query?.localityId ? { localityId: query.localityId } : {}),
          ...(query?.onlyZeroResults !== undefined ? { onlyZeroResults: query.onlyZeroResults } : {}),
        },
      })
      .then((r) => r.data);
  },

  /* Feeds/matérias das páginas de cidade */
  listFeeds(
    query?: PageQuery & { status?: FeedStatus; localityId?: string; categoryId?: string }
  ): Promise<Page<FeedDto>> {
    return api
      .get<Page<FeedDto>>('/v1/backoffice/feeds', {
        params: {
          ...toParams(query),
          ...(query?.status ? { status: query.status } : {}),
          ...(query?.localityId ? { localityId: query.localityId } : {}),
          ...(query?.categoryId ? { categoryId: query.categoryId } : {}),
        },
      })
      .then((r) => r.data);
  },
  getFeed(id: string): Promise<FeedDto> {
    return api.get<FeedDto>(`/v1/backoffice/feeds/${id}`).then((r) => r.data);
  },
  createFeed(payload: FeedRequestDto): Promise<FeedDto> {
    return api.post<FeedDto>('/v1/backoffice/feeds', payload).then((r) => r.data);
  },
  updateFeed(id: string, payload: FeedRequestDto): Promise<FeedDto> {
    return api.put<FeedDto>(`/v1/backoffice/feeds/${id}`, payload).then((r) => r.data);
  },
  deleteFeed(id: string): Promise<void> {
    return api.delete(`/v1/backoffice/feeds/${id}`).then(() => undefined);
  },

  /* Curadoria das trilhas do /explorar (card 222) - ao lado dos feeds porque é
     a mesma natureza de trabalho editorial e a mesma autorização. */

  /**
   * O vocabulário que o filtro da trilha pode escolher.
   *
   * `/v1/merchants/filters` - a rota PÚBLICA da vitrine - e não
   * `/v1/backoffice/amenities`, apesar de o backoffice ter a sua. São a mesma
   * lista (as duas saem do `DiscoveryCatalogService`, só as ativas, que é
   * exatamente o que o core valida em `TrailFilters`), mas a rota do backoffice
   * exige `ROLE_BACKOFFICE_AMENITY_READ` - que quem cura trilha não tem, porque
   * este card não criou papel novo e a autorização é a de Feeds. Pela rota do
   * backoffice, o curador abriria o formulário e veria a lista de comodidades
   * vazia por 403, sem nenhuma pista do motivo.
   *
   * De quebra vem `priceRanges` com símbolo e rótulo prontos, que a tela
   * precisa desenhar do mesmo jeito que a vitrine desenha.
   */
  getDiscoveryFilters(): Promise<MerchantDiscoveryOptionsDto> {
    return api.get<MerchantDiscoveryOptionsDto>('/v1/merchants/filters').then((r) => r.data);
  },

  listTrails(
    query?: PageQuery & { active?: boolean; localityId?: string }
  ): Promise<Page<BackofficeTrailDto>> {
    return api
      .get<Page<BackofficeTrailDto>>('/v1/backoffice/explore/trails', {
        params: {
          ...toParams(query),
          ...(query?.active !== undefined ? { active: query.active } : {}),
          ...(query?.localityId ? { localityId: query.localityId } : {}),
        },
      })
      .then((r) => r.data);
  },
  getTrail(id: string): Promise<BackofficeTrailDto> {
    return api.get<BackofficeTrailDto>(`/v1/backoffice/explore/trails/${id}`).then((r) => r.data);
  },
  createTrail(payload: TrailRequestDto): Promise<BackofficeTrailDto> {
    return api.post<BackofficeTrailDto>('/v1/backoffice/explore/trails', payload).then((r) => r.data);
  },
  updateTrail(id: string, payload: TrailRequestDto): Promise<BackofficeTrailDto> {
    return api
      .put<BackofficeTrailDto>(`/v1/backoffice/explore/trails/${id}`, payload)
      .then((r) => r.data);
  },
  deleteTrail(id: string): Promise<void> {
    return api.delete(`/v1/backoffice/explore/trails/${id}`).then(() => undefined);
  },
  /**
   * Quantas lojas a trilha pega, ANTES de salvar.
   *
   * Não é enfeite: é o §4 da spec 036. O piso de publicação é
   * `TRAIL_MIN_MERCHANTS` (2), ele não viaja na resposta e o servidor não
   * bloqueia salvar abaixo dele - quem avisa é a tela.
   */
  previewTrail(payload: TrailPreviewRequestDto): Promise<TrailPreviewDto> {
    return api
      .post<TrailPreviewDto>('/v1/backoffice/explore/trails/preview', payload)
      .then((r) => r.data);
  },

  /* Coleções editoriais (listas curadas com endereço próprio) */
  listCollections(
    query?: PageQuery & { status?: CollectionStatus; localityId?: string }
  ): Promise<Page<CollectionDto>> {
    return api
      .get<Page<CollectionDto>>('/v1/backoffice/collections', {
        params: {
          ...toParams(query),
          ...(query?.status ? { status: query.status } : {}),
          ...(query?.localityId ? { localityId: query.localityId } : {}),
        },
      })
      .then((r) => r.data);
  },
  getCollection(id: string): Promise<CollectionDto> {
    return api.get<CollectionDto>(`/v1/backoffice/collections/${id}`).then((r) => r.data);
  },
  createCollection(payload: CollectionRequestDto): Promise<CollectionDto> {
    return api.post<CollectionDto>('/v1/backoffice/collections', payload).then((r) => r.data);
  },
  updateCollection(id: string, payload: CollectionRequestDto): Promise<CollectionDto> {
    return api.put<CollectionDto>(`/v1/backoffice/collections/${id}`, payload).then((r) => r.data);
  },
  deleteCollection(id: string): Promise<void> {
    return api.delete(`/v1/backoffice/collections/${id}`).then(() => undefined);
  },

  /* Promotion moderation */
  listPromotions(query?: PageQuery & { status?: string }): Promise<Page<PromotionDto>> {
    return api
      .get<Page<PromotionDto>>('/v1/backoffice/promotions', {
        params: { ...toParams(query), ...(query?.status ? { status: query.status } : {}) },
      })
      .then((r) => r.data);
  },
  moderatePromotion(id: string, payload: PromotionModerationDto): Promise<PromotionDto> {
    return api.post<PromotionDto>(`/v1/backoffice/promotions/${id}/moderate`, payload).then((r) => r.data);
  },

  /* Leads (captured from the site's "Fale com a gente" form) */
  getLeadsBoard(): Promise<LeadBoardDto> {
    return api.get<LeadBoardDto>('/v1/backoffice/leads/board').then((r) => r.data);
  },
  listLeads(query?: string): Promise<LeadDto[]> {
    return api
      .get<LeadDto[]>('/v1/backoffice/leads', { params: query ? { query } : {} })
      .then((r) => r.data);
  },
  updateLeadStatus(id: string, payload: LeadStatusUpdateDto): Promise<LeadDto> {
    return api.put<LeadDto>(`/v1/backoffice/leads/${id}/status`, payload).then((r) => r.data);
  },
  deleteLead(id: string): Promise<void> {
    return api.delete(`/v1/backoffice/leads/${id}`).then(() => undefined);
  },

  /* Disparos - instâncias (números) usadas nas campanhas.
     Servidor e chave da Evolution são configuração do backend, não campos de tela:
     ver `getMessagingChannel`. */
  getMessagingChannel(): Promise<MessagingChannelStatusDto> {
    return api
      .get<MessagingChannelStatusDto>('/v1/backoffice/messaging/instances/channel')
      .then((r) => r.data);
  },
  listMessagingInstances(): Promise<MessagingInstanceDto[]> {
    return api.get<MessagingInstanceDto[]>('/v1/backoffice/messaging/instances').then((r) => r.data);
  },
  /** Cria a instância na Evolution (ou adota a existente) e devolve o QR do pareamento. */
  createMessagingInstance(payload: MessagingInstanceCreateRequestDto): Promise<MessagingInstanceCreatedDto> {
    return api
      .post<MessagingInstanceCreatedDto>('/v1/backoffice/messaging/instances', payload)
      .then((r) => r.data);
  },
  updateMessagingInstance(
    id: string,
    payload: MessagingInstanceUpdateRequestDto
  ): Promise<MessagingInstanceDto> {
    return api.put<MessagingInstanceDto>(`/v1/backoffice/messaging/instances/${id}`, payload).then((r) => r.data);
  },
  deleteMessagingInstance(id: string): Promise<void> {
    return api.delete(`/v1/backoffice/messaging/instances/${id}`).then(() => undefined);
  },
  /** Estado da conexão - é o que a tela consulta no polling do QR. */
  getMessagingConnection(id: string): Promise<MessagingConnectionDto> {
    return api
      .get<MessagingConnectionDto>(`/v1/backoffice/messaging/instances/${id}/connection`)
      .then((r) => r.data);
  },
  /** Novo QR Code: o da Evolution expira em segundos. */
  connectMessagingInstance(id: string): Promise<MessagingQrCodeDto> {
    return api
      .post<MessagingQrCodeDto>(`/v1/backoffice/messaging/instances/${id}/connect`)
      .then((r) => r.data);
  },
  sendMessagingTest(id: string, payload: MessagingTestRequestDto): Promise<void> {
    return api.post(`/v1/backoffice/messaging/instances/${id}/test`, payload).then(() => undefined);
  },

  /* Disparos - templates */
  listMessageTemplates(): Promise<MessageTemplateDto[]> {
    return api.get<MessageTemplateDto[]>('/v1/backoffice/messaging/templates').then((r) => r.data);
  },
  listMessageTags(): Promise<MessageTagDto[]> {
    return api.get<MessageTagDto[]>('/v1/backoffice/messaging/templates/tags').then((r) => r.data);
  },
  createMessageTemplate(payload: MessageTemplateRequestDto): Promise<MessageTemplateDto> {
    return api.post<MessageTemplateDto>('/v1/backoffice/messaging/templates', payload).then((r) => r.data);
  },
  updateMessageTemplate(id: string, payload: MessageTemplateRequestDto): Promise<MessageTemplateDto> {
    return api.put<MessageTemplateDto>(`/v1/backoffice/messaging/templates/${id}`, payload).then((r) => r.data);
  },
  deleteMessageTemplate(id: string): Promise<void> {
    return api.delete(`/v1/backoffice/messaging/templates/${id}`).then(() => undefined);
  },
  previewMessageTemplate(payload: TemplatePreviewRequestDto): Promise<TemplatePreviewDto> {
    return api
      .post<TemplatePreviewDto>('/v1/backoffice/messaging/templates/preview', payload)
      .then((r) => r.data);
  },

  /* Disparos - listas de contato */
  listContactLists(): Promise<ContactListDto[]> {
    return api.get<ContactListDto[]>('/v1/backoffice/messaging/contact-lists').then((r) => r.data);
  },
  createContactList(payload: ContactListRequestDto): Promise<ContactListDto> {
    return api.post<ContactListDto>('/v1/backoffice/messaging/contact-lists', payload).then((r) => r.data);
  },
  updateContactList(id: string, payload: ContactListRequestDto): Promise<ContactListDto> {
    return api.put<ContactListDto>(`/v1/backoffice/messaging/contact-lists/${id}`, payload).then((r) => r.data);
  },
  deleteContactList(id: string): Promise<void> {
    return api.delete(`/v1/backoffice/messaging/contact-lists/${id}`).then(() => undefined);
  },
  listContactListMembers(id: string, query?: PageQuery): Promise<Page<ContactListMemberDto>> {
    return api
      .get<Page<ContactListMemberDto>>(`/v1/backoffice/messaging/contact-lists/${id}/members`, {
        params: toParams(query),
      })
      .then((r) => r.data);
  },
  /** Uma porta para as três entradas: formulário, texto colado e planilha. */
  importContactListMembers(id: string, payload: ContactImportRequestDto): Promise<ContactImportReportDto> {
    return api
      .post<ContactImportReportDto>(`/v1/backoffice/messaging/contact-lists/${id}/members`, payload)
      .then((r) => r.data);
  },
  deleteContactListMember(id: string, memberId: string): Promise<void> {
    return api
      .delete(`/v1/backoffice/messaging/contact-lists/${id}/members/${memberId}`)
      .then(() => undefined);
  },
  /** N origens (mínimo 2) numa lista nova. As origens são desativadas, não apagadas. */
  mergeContactLists(payload: ContactListMergeRequestDto): Promise<ContactListMergeReportDto> {
    return api
      .post<ContactListMergeReportDto>('/v1/backoffice/messaging/contact-lists/merge', payload)
      .then((r) => r.data);
  },

  /* Disparos - contatos (registro global por número: bloqueio e WhatsApp).
     Endereçados pelo telefone, não por id: a chave do contato é o próprio número. */
  listMessagingContacts(
    query?: PageQuery & {
      blocked?: boolean;
      whatsappStatus?: WhatsappStatus;
      contactListId?: string;
      minLists?: number;
      /** `true` = só parceiros ativos, `false` = só quem não é, ausente = não filtra. */
      partner?: boolean;
    }
  ): Promise<Page<MessagingContactDto>> {
    return api
      .get<Page<MessagingContactDto>>('/v1/backoffice/messaging/contacts', {
        params: {
          ...toParams(query),
          ...(query?.blocked !== undefined ? { blocked: query.blocked } : {}),
          ...(query?.whatsappStatus ? { whatsappStatus: query.whatsappStatus } : {}),
          ...(query?.contactListId ? { contactListId: query.contactListId } : {}),
          ...(query?.minLists ? { minLists: query.minLists } : {}),
          ...(query?.partner !== undefined ? { partner: query.partner } : {}),
        },
      })
      .then((r) => r.data);
  },
  getMessagingContact(phoneNumber: string): Promise<MessagingContactDto> {
    return api
      .get<MessagingContactDto>(`/v1/backoffice/messaging/contacts/${phoneNumber}`)
      .then((r) => r.data);
  },
  blockMessagingContact(phoneNumber: string, payload: ContactBlockRequestDto): Promise<MessagingContactDto> {
    return api
      .post<MessagingContactDto>(`/v1/backoffice/messaging/contacts/${phoneNumber}/block`, payload)
      .then((r) => r.data);
  },
  /** Liberar não reenfileira nada: quem quer tentar de novo usa o "Reenviar falhas". */
  unblockMessagingContact(phoneNumber: string, payload: ContactBlockRequestDto): Promise<MessagingContactDto> {
    return api
      .post<MessagingContactDto>(`/v1/backoffice/messaging/contacts/${phoneNumber}/unblock`, payload)
      .then((r) => r.data);
  },
  /** Revalidação sob demanda: por lista inteira ou pelos números informados. */
  checkMessagingContactsWhatsapp(payload: WhatsappCheckRequestDto): Promise<WhatsappCheckReportDto> {
    return api
      .post<WhatsappCheckReportDto>('/v1/backoffice/messaging/contacts/whatsapp-check', payload)
      .then((r) => r.data);
  },

  /* Disparos - opt-out (global, por número) */
  listMessagingOptOuts(): Promise<MessagingOptOutDto[]> {
    return api.get<MessagingOptOutDto[]>('/v1/backoffice/messaging/opt-outs').then((r) => r.data);
  },
  addMessagingOptOut(payload: MessagingOptOutRequestDto): Promise<MessagingOptOutDto> {
    return api.post<MessagingOptOutDto>('/v1/backoffice/messaging/opt-outs', payload).then((r) => r.data);
  },
  deleteMessagingOptOut(id: string): Promise<void> {
    return api.delete(`/v1/backoffice/messaging/opt-outs/${id}`).then(() => undefined);
  },

  /* Disparos - campanhas. Não há "enviar": criar enfileira, iniciar libera o drenador. */
  listMessageCampaigns(
    query?: PageQuery & { status?: MessageCampaignStatus }
  ): Promise<Page<MessageCampaignDto>> {
    return api
      .get<Page<MessageCampaignDto>>('/v1/backoffice/messaging/campaigns', {
        params: { ...toParams(query), ...(query?.status ? { status: query.status } : {}) },
      })
      .then((r) => r.data);
  },
  getMessageCampaign(id: string): Promise<MessageCampaignDto> {
    return api.get<MessageCampaignDto>(`/v1/backoffice/messaging/campaigns/${id}`).then((r) => r.data);
  },
  getMessageCampaignCounters(id: string): Promise<CampaignCountersDto> {
    return api
      .get<CampaignCountersDto>(`/v1/backoffice/messaging/campaigns/${id}/counters`)
      .then((r) => r.data);
  },
  listMessageCampaignDispatches(
    id: string,
    query?: PageQuery & { status?: MessageDispatchStatus }
  ): Promise<Page<MessageDispatchDto>> {
    return api
      .get<Page<MessageDispatchDto>>(`/v1/backoffice/messaging/campaigns/${id}/dispatches`, {
        params: { ...toParams(query), ...(query?.status ? { status: query.status } : {}) },
      })
      .then((r) => r.data);
  },
  createMessageCampaign(payload: MessageCampaignRequestDto): Promise<MessageCampaignDto> {
    return api.post<MessageCampaignDto>('/v1/backoffice/messaging/campaigns', payload).then((r) => r.data);
  },
  startMessageCampaign(id: string): Promise<MessageCampaignDto> {
    return api
      .post<MessageCampaignDto>(`/v1/backoffice/messaging/campaigns/${id}/start`)
      .then((r) => r.data);
  },
  /** `scheduledAt` nulo desmarca e devolve a campanha ao início manual. */
  scheduleMessageCampaign(id: string, scheduledAt: string | null): Promise<MessageCampaignDto> {
    return api
      .put<MessageCampaignDto>(`/v1/backoffice/messaging/campaigns/${id}/schedule`, { scheduledAt })
      .then((r) => r.data);
  },
  /** Devolve à fila quem falhou. Eles voltam a disputar o ritmo e a cota da instância. */
  retryFailedMessageCampaign(id: string): Promise<MessageCampaignDto> {
    return api
      .post<MessageCampaignDto>(`/v1/backoffice/messaging/campaigns/${id}/retry-failed`)
      .then((r) => r.data);
  },
  pauseMessageCampaign(id: string): Promise<MessageCampaignDto> {
    return api
      .post<MessageCampaignDto>(`/v1/backoffice/messaging/campaigns/${id}/pause`)
      .then((r) => r.data);
  },
  cancelMessageCampaign(id: string): Promise<MessageCampaignDto> {
    return api
      .post<MessageCampaignDto>(`/v1/backoffice/messaging/campaigns/${id}/cancel`)
      .then((r) => r.data);
  },
  deleteMessageCampaign(id: string): Promise<void> {
    return api.delete(`/v1/backoffice/messaging/campaigns/${id}`).then(() => undefined);
  },

  /* Plans */
  listPlans(query?: PageQuery): Promise<Page<PlanDto>> {
    return api.get<Page<PlanDto>>('/v1/backoffice/plans', { params: toParams(query) }).then((r) => r.data);
  },
  createPlan(payload: PlanRequestDto): Promise<PlanDto> {
    return api.post<PlanDto>('/v1/backoffice/plans', payload).then((r) => r.data);
  },
  updatePlan(id: string, payload: PlanRequestDto): Promise<PlanDto> {
    return api.put<PlanDto>(`/v1/backoffice/plans/${id}`, payload).then((r) => r.data);
  },
  setPlanStatus(id: string, action: 'activate' | 'archive'): Promise<PlanDto> {
    return api.post<PlanDto>(`/v1/backoffice/plans/${id}/${action}`).then((r) => r.data);
  },

  /* Kardume Pass plans (planos do consumidor) */
  listPassPlans(query?: PageQuery): Promise<Page<PassPlanDto>> {
    return api.get<Page<PassPlanDto>>('/v1/backoffice/pass-plans', { params: toParams(query) }).then((r) => r.data);
  },
  createPassPlan(payload: PassPlanRequestDto): Promise<PassPlanDto> {
    return api.post<PassPlanDto>('/v1/backoffice/pass-plans', payload).then((r) => r.data);
  },
  updatePassPlan(id: string, payload: PassPlanRequestDto): Promise<PassPlanDto> {
    return api.put<PassPlanDto>(`/v1/backoffice/pass-plans/${id}`, payload).then((r) => r.data);
  },
  setPassPlanStatus(id: string, action: 'activate' | 'archive'): Promise<PassPlanDto> {
    return api.post<PassPlanDto>(`/v1/backoffice/pass-plans/${id}/${action}`).then((r) => r.data);
  },

  /* Influenciadores (spec 038) */
  listInfluencers(query?: PageQuery & { term?: string; active?: boolean }): Promise<Page<InfluencerDto>> {
    return api
      .get<Page<InfluencerDto>>('/v1/backoffice/influencers', {
        params: {
          ...toParams(query),
          ...(query?.term ? { term: query.term } : {}),
          ...(query?.active === undefined ? {} : { active: query.active }),
        },
      })
      .then((r) => r.data);
  },
  getInfluencerOverview(): Promise<InfluencerOverviewDto> {
    return api.get<InfluencerOverviewDto>('/v1/backoffice/influencers/overview').then((r) => r.data);
  },
  getInfluencer(id: string): Promise<InfluencerDto> {
    return api.get<InfluencerDto>(`/v1/backoffice/influencers/${id}`).then((r) => r.data);
  },
  createInfluencer(payload: CreateInfluencerRequestDto): Promise<InfluencerDto> {
    return api.post<InfluencerDto>('/v1/backoffice/influencers', payload).then((r) => r.data);
  },
  updateInfluencer(id: string, payload: UpdateInfluencerRequestDto): Promise<InfluencerDto> {
    return api.put<InfluencerDto>(`/v1/backoffice/influencers/${id}`, payload).then((r) => r.data);
  },
  deleteInfluencer(id: string): Promise<void> {
    return api.delete(`/v1/backoffice/influencers/${id}`).then(() => undefined);
  },
  /** Upsert: a tela é uma linha por plano, não um par criar/editar. */
  saveInfluencerRate(id: string, payload: InfluencerRateRequestDto): Promise<InfluencerRateDto> {
    return api.post<InfluencerRateDto>(`/v1/backoffice/influencers/${id}/rates`, payload).then((r) => r.data);
  },
  deleteInfluencerRate(id: string, rateId: string): Promise<void> {
    return api.delete(`/v1/backoffice/influencers/${id}/rates/${rateId}`).then(() => undefined);
  },
  listCommissions(
    query?: PageQuery & { influencerId?: string; status?: CommissionStatus; competence?: string }
  ): Promise<Page<CommissionDto>> {
    return api
      .get<Page<CommissionDto>>('/v1/backoffice/influencers/commissions', {
        params: {
          ...toParams(query),
          ...(query?.influencerId ? { influencerId: query.influencerId } : {}),
          ...(query?.status ? { status: query.status } : {}),
          ...(query?.competence ? { competence: query.competence } : {}),
        },
      })
      .then((r) => r.data);
  },
  cancelCommission(commissionId: string, reason: string): Promise<CommissionDto> {
    return api
      .post<CommissionDto>(`/v1/backoffice/influencers/commissions/${commissionId}/cancel`, { reason })
      .then((r) => r.data);
  },
  listPayouts(query?: PageQuery & { influencerId?: string; status?: PayoutStatus }): Promise<Page<PayoutDto>> {
    return api
      .get<Page<PayoutDto>>('/v1/backoffice/influencers/payouts', {
        params: {
          ...toParams(query),
          ...(query?.influencerId ? { influencerId: query.influencerId } : {}),
          ...(query?.status ? { status: query.status } : {}),
        },
      })
      .then((r) => r.data);
  },
  createPayout(influencerId: string, payload: { competence: string; notes?: string }): Promise<PayoutDto> {
    return api.post<PayoutDto>(`/v1/backoffice/influencers/${influencerId}/payouts`, payload).then((r) => r.data);
  },
  payPayout(payoutId: string, payload: { paymentReference?: string; notes?: string }): Promise<PayoutDto> {
    return api.post<PayoutDto>(`/v1/backoffice/influencers/payouts/${payoutId}/pay`, payload).then((r) => r.data);
  },
  cancelPayout(payoutId: string): Promise<PayoutDto> {
    return api.post<PayoutDto>(`/v1/backoffice/influencers/payouts/${payoutId}/cancel`).then((r) => r.data);
  },

  /* Subscriptions */
  listSubscriptions(query?: PageQuery): Promise<Page<SubscriptionDto>> {
    return api.get<Page<SubscriptionDto>>('/v1/backoffice/subscriptions', { params: toParams(query) }).then((r) => r.data);
  },
  createSubscription(payload: {
    merchantId: string;
    planId: string;
    billingPeriod?: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
    price?: number;
    trialDays?: number;
  }): Promise<SubscriptionDto> {
    return api.post<SubscriptionDto>('/v1/backoffice/subscriptions', payload).then((r) => r.data);
  },
  cancelSubscription(id: string): Promise<SubscriptionDto> {
    return api.post<SubscriptionDto>(`/v1/backoffice/subscriptions/${id}/cancel`).then((r) => r.data);
  },
  /** Troca o plano da assinatura a partir do próximo ciclo (#100). */
  changeSubscriptionPlan(
    id: string,
    payload: { planId: string; price?: number; billingPeriod?: 'MONTHLY' | 'QUARTERLY' | 'YEARLY'; reason?: string }
  ): Promise<SubscriptionDto> {
    return api.post<SubscriptionDto>(`/v1/backoffice/subscriptions/${id}/change-plan`, payload).then((r) => r.data);
  },
  /** Concede carência: segura o corte de acesso até `graceUntil` (#100). */
  grantGracePeriod(id: string, payload: { graceUntil: string; reason?: string }): Promise<SubscriptionDto> {
    return api.post<SubscriptionDto>(`/v1/backoffice/subscriptions/${id}/grace-period`, payload).then((r) => r.data);
  },
  /** Ativa trial manual pelos dias informados (#100). */
  startTrial(id: string, payload: { trialDays: number; reason?: string }): Promise<SubscriptionDto> {
    return api.post<SubscriptionDto>(`/v1/backoffice/subscriptions/${id}/trial`, payload).then((r) => r.data);
  },

  /* Invoices (billing) */
  listInvoices(query?: PageQuery): Promise<Page<InvoiceDto>> {
    return api.get<Page<InvoiceDto>>('/v1/backoffice/invoices', { params: toParams(query) }).then((r) => r.data);
  },
  payInvoice(id: string): Promise<InvoiceDto> {
    return api.post<InvoiceDto>(`/v1/backoffice/invoices/${id}/pay`).then((r) => r.data);
  },
  reissueInvoice(id: string): Promise<InvoiceDto> {
    return api.post<InvoiceDto>(`/v1/backoffice/invoices/${id}/reissue`).then((r) => r.data);
  },

  /* Audit logs */
  listAuditLogs(
    query?: PageQuery & { module?: string; actor?: string; resourceId?: string; action?: string; search?: string }
  ): Promise<Page<AuditLogDto>> {
    return api
      .get<Page<AuditLogDto>>("/v1/backoffice/audit-logs", {
        params: {
          ...toParams(query),
          ...(query?.module ? { module: query.module } : {}),
          ...(query?.actor ? { actor: query.actor } : {}),
          ...(query?.resourceId ? { resourceId: query.resourceId } : {}),
          ...(query?.action ? { action: query.action } : {}),
          ...(query?.search ? { search: query.search } : {}),
        },
      })
      .then((r) => r.data);
  },

  /* Employees (access) */
  listEmployees(query?: PageQuery): Promise<Page<BackofficeEmployeeDto>> {
    return api.get<Page<BackofficeEmployeeDto>>('/v1/backoffice/employees', { params: toParams(query) }).then((r) => r.data);
  },
  createEmployee(payload: BackofficeEmployeeRequestDto): Promise<BackofficeEmployeeDto> {
    return api.post<BackofficeEmployeeDto>('/v1/backoffice/employees', payload).then((r) => r.data);
  },
  updateEmployee(id: string, payload: BackofficeEmployeeRequestDto): Promise<BackofficeEmployeeDto> {
    return api.put<BackofficeEmployeeDto>(`/v1/backoffice/employees/${id}`, payload).then((r) => r.data);
  },
  /**
   * Generates a new temporary password and e-mails it again. Rejects (changing
   * nothing) when the message cannot be delivered - the caller should surface the
   * reason, since a silent failure would leave the person with neither password.
   */
  resendEmployeeCredentials(id: string): Promise<BackofficeEmployeeDto> {
    return api
      .post<BackofficeEmployeeDto>(`/v1/backoffice/employees/${id}/resend-credentials`)
      .then((r) => r.data);
  },
  /**
   * Exclusão lógica do usuário do backoffice (credencial e sessões vão junto).
   * Rejeita quando sobraria o painel sem nenhum administrador.
   */
  deleteEmployee(id: string): Promise<void> {
    return api.delete(`/v1/backoffice/employees/${id}`).then(() => undefined);
  },
  /* E-mail channel diagnostics */
  getEmailStatus(): Promise<EmailChannelStatusDto> {
    return api.get<EmailChannelStatusDto>('/v1/backoffice/email/status').then((r) => r.data);
  },
  sendTestEmail(to: string): Promise<void> {
    return api.post('/v1/backoffice/email/test', { to }).then(() => undefined);
  },

  /* Access profiles ("perfis de acesso") */
  listPermissions(): Promise<string[]> {
    return api.get<string[]>('/v1/backoffice/access/permissions').then((r) => r.data);
  },
  listAccessProfiles(): Promise<BackofficeAccessProfileDto[]> {
    return api.get<BackofficeAccessProfileDto[]>('/v1/backoffice/access/profiles').then((r) => r.data);
  },
  createAccessProfile(payload: BackofficeAccessProfileRequestDto): Promise<BackofficeAccessProfileDto> {
    return api.post<BackofficeAccessProfileDto>('/v1/backoffice/access/profiles', payload).then((r) => r.data);
  },
  updateAccessProfilePermissions(
    id: string,
    payload: AccessProfilePermissionsUpdateDto
  ): Promise<BackofficeAccessProfileDto> {
    return api
      .put<BackofficeAccessProfileDto>(`/v1/backoffice/access/profiles/${id}/permissions`, payload)
      .then((r) => r.data);
  },
  getEmployeeAccess(employeeId: string): Promise<BackofficeEmployeeAccessDetailDto> {
    return api
      .get<BackofficeEmployeeAccessDetailDto>(`/v1/backoffice/employees/${employeeId}`)
      .then((r) => r.data);
  },
};
